import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, Subscription, filter, map } from 'rxjs';
import { Schedule } from 'src/app/model/schedule';
import { selectUser } from 'src/app/store/user/user.selector';
import { User } from 'src/app/model/user';
import { LIST_COLOR, SCHEDULE_DEFAULT_REPETITION, SCHEDULE_REPETITIONS, WEEK_DAYS } from 'src/app/conf/schedule';
import { CustomValidators } from 'src/app/validators/custom.validators';
import moment from 'moment';
import { EScheduleRepeat } from 'src/app/model/schedule-repeat';
import * as ScheduleActions from 'src/app/store/schedule/schedule.actions';
import { selectSceneries } from 'src/app/store/scenery/scenery.selector';
import { selectCalendars } from 'src/app/store/calendar/calendar.selector';
import { selectScheduleAdded, selectScheduleAdding, selectScheduleEdited, selectScheduleEditing } from 'src/app/store/schedule/schedule.selector';
import { Scenery } from 'src/app/model/scenery';
import { Calendar } from 'src/app/model/calendar';
import { TIMEZONES } from 'src/app/conf/timezones';
import { CalendarException } from 'src/app/model/calendar-exception';
import { StandardDesignParameters, StandardNewValidator, StandardNewValidatorStepTabs, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	templateUrl: './schedule.dialog.component.html',
	styles: `
		#scheduleDialog {
			min-height: 24vw;
		}
	`
})
export class ScheduleDialogComponent implements OnInit, OnDestroy {
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;
	sceneries$: Observable<Scenery[] | undefined>;
	sceneriesSub: Subscription | undefined;
	calendars$: Observable<Calendar[] | undefined>;
	calendarsSub: Subscription | undefined;
	scheduleAdding$: Observable<boolean | undefined>;
	scheduleAddingSub: Subscription | undefined;
	scheduleAdded$: Observable<boolean | undefined>;
	scheduleAddedSub: Subscription | undefined;
	scheduleEditing$: Observable<boolean | undefined>;
	scheduleEditingSub: Subscription | undefined;
	scheduleEdited$: Observable<boolean | undefined>;
	scheduleEditedSub: Subscription | undefined;

	exceptions: CalendarException[];
	scheduleForm: FormGroup;

	currentUser: User;
	sceneries: Scenery[];
	calendars: Calendar[];
	scheduleAdding: boolean;
	scheduleEditing: boolean;
	schedule: Schedule;

	filteredTimezone: any[];
	timezones = TIMEZONES;

	repetitions = SCHEDULE_REPETITIONS;
	daysOfWeek = WEEK_DAYS;

	today: Date = new Date();
	minDate: Date = new Date(moment().startOf('day').toDate());

	listColor = LIST_COLOR;

	repeatName: string;

	activeIndex: number = 0;

	// design
	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private fb: FormBuilder,
	) {
		if (config.data !== undefined && config.data.schedule !== undefined) {
			this.schedule = { ...config.data.schedule };
			if (!this.schedule.EveryKey) {
				this.schedule.EveryKey = SCHEDULE_DEFAULT_REPETITION.Value;
			}
			// this.minDate = new Date(this.schedule.Start);
		} else {
			this.schedule = new Schedule();
		}
		if (this.schedule.End === null) {
			this.schedule.End = undefined;
		}
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();
		this.repeatName = 'not_repeating';
		StandardNewValidator.setFirstCompile(true);

		StandardNewValidatorStepTabs.firstTabfields = [];
		StandardNewValidatorStepTabs.secondTabfields = [];
		StandardNewValidatorStepTabs.thirdTabfields = [];

		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.startDate, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.endDate, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.startTime, 0);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.timezone, 0);

		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.everyKey, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.everyValue, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.weekDays, 1);
		StandardNewValidatorStepTabs.pushFieldOnTab(this.f.monthDay, 1);
	}

	buildForm(): void {
		const currentWeekDays = this.schedule.WeekDays ? this.schedule.WeekDays.split(',') : [];
		const wDayArr = WEEK_DAYS.map((weekday) => this.fb.control(currentWeekDays.includes(weekday.Value)));
		const scenery = this.sceneries.filter(scen => scen.Id === this.schedule.SceneryId);
		this.scheduleForm = new FormGroup({
			'name': new FormControl(this.schedule.Name, Validators.required),
			'description': new FormControl(this.schedule.Description),
			'scenery': new FormControl(scenery.length > 0 ? scenery[0] : null, Validators.required),
			'startDate': new FormControl(new Date(this.schedule.Start), Validators.required),
			'startTime': new FormControl(this.getTime(this.schedule.Hours, this.schedule.Minutes), Validators.required),
			'endDate': new FormControl(this.schedule.End !== undefined ? new Date(this.schedule.End) : ''),
			'everyKey': new FormControl(this.schedule.EveryKey),
			'everyValue': new FormControl({ value: this.schedule.EveryValue, disabled: false }, [Validators.required, Validators.pattern('[0-9]+'), Validators.min(1)]),
			'monthDay': new FormControl({
				value: this.schedule.MonthDay,
				disabled: false
			}, [Validators.required, Validators.min(1), Validators.max(31), Validators.pattern('[0-9]+')]),
			'weekDays': this.fb.array(wDayArr, CustomValidators.multipleCheckboxRequireOne),
			'calendar': new FormControl(this.schedule.CalendarId),
			'timezone': new FormControl(this.schedule.Tz, Validators.required),
			'color': new FormControl(this.schedule.Color)
		});
		if (this.schedule.CalendarId) {
			this.calendars.forEach(calendar => {
				if (calendar.Id === this.schedule.CalendarId) {
					this.f.calendar.value = calendar;
				}
			});
		} else {
			this.calendars.forEach(calendar => {
				if (calendar.Id === -1) {
					this.f.calendar.value = calendar;
				}
			});
		}
		if (this.schedule.Id) {
			this.checkEveryKey(this.schedule.EveryKey);
		} else {
			this.f.weekDays.disable();
		}
	}

	changeActiveTabClick(index: number) {
		// if (this.canSwitchTab()) {
		this.activeIndex = index;
	}

	checkEveryKey(val: string): void {
		this.schedule.EveryKey = val;
		switch (val) {
			case EScheduleRepeat.Weeks:
				this.f.everyValue.enable();
				this.f.weekDays.enable();
				this.f.monthDay.disable();
				break;
			case EScheduleRepeat.Months:
				this.f.everyValue.enable();
				this.f.weekDays.disable();
				this.f.monthDay.enable();
				break;
			case EScheduleRepeat.Days:
				this.f.everyValue.enable();
				this.f.weekDays.disable();
				this.f.monthDay.disable();
				break;
			default: // EScheduleRepeat.NotRepeat
				this.f.everyValue.disable();
				this.f.weekDays.disable();
				this.f.monthDay.disable();
				break;
		}
	}

	initSelectors(): void {
		this.currentUser$ = this.store.select(selectUser);
		this.sceneries$ = this.store.select(selectSceneries);
		this.calendars$ = this.store.select(selectCalendars);
		this.scheduleAdding$ = this.store.select(selectScheduleAdding);
		this.scheduleAdded$ = this.store.select(selectScheduleAdded);
		this.scheduleEditing$ = this.store.select(selectScheduleEditing);
		this.scheduleEdited$ = this.store.select(selectScheduleEdited);
	}

	subscribeAll(): void {
		this.currentUserSub = this.currentUser$?.subscribe((currentUser) => {
			this.currentUser = currentUser;
		});
		this.sceneriesSub = this.sceneries$?.subscribe((sceneries) => {
			this.sceneries = sceneries;
		});
		this.calendarsSub = this.calendars$?.subscribe((calendars) => {
			const calendar = new Calendar;
			this.calendars = [...calendars];
			calendar.Id = -1;
			calendar.Name = "----";
			calendar.Description = "";
			this.calendars.unshift(calendar);
		});
		this.scheduleAddingSub = this.scheduleAdding$?.subscribe((scheduleAdding) => {
			this.scheduleAdding = scheduleAdding;
		});
		this.scheduleAddedSub = this.scheduleAdded$?.pipe(filter(edit => !!edit))
			.subscribe(() => {
				this.resetClose();
			});
		this.scheduleEditingSub = this.scheduleEditing$?.subscribe((scheduleEditing) => {
			this.scheduleEditing = scheduleEditing;
		});
		this.scheduleEditedSub = this.scheduleEdited$?.pipe(filter(edit => !!edit))
			.subscribe(() => {
				this.resetClose();
			});
	}

	ngOnDestroy(): void {
		this.currentUserSub?.unsubscribe();
		this.sceneriesSub?.unsubscribe();
		this.calendarsSub?.unsubscribe();
		this.scheduleAddingSub?.unsubscribe();
		this.scheduleAddedSub?.unsubscribe();
		this.scheduleEditingSub?.unsubscribe();
		this.scheduleEditedSub?.unsubscribe();
	}

	setColorSchedule(color: string): void {
		color = color.toUpperCase();
		this.f.color.value = color;
	}

	getColorSchedule(color: string): boolean {
		color = color.toUpperCase();
		const fColor = this.f.color.value.toUpperCase();
		return fColor === color;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.scheduleForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.scheduleForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	canSwitchTab(): boolean {
		return StandardNewValidatorStepTabs.canSwitchTab(this.activeIndex);
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const startDate = moment(this.f.startDate.value).format('YYYY-MM-DD') + 'T00:00:00.000Z';
		let startTime = ''
		if (typeof this.f.startTime.value === 'string') {
			startTime = this.f.startTime.value;
		} else {
			startTime = moment(this.f.startTime.value).format('HH:mm');
		}
		let endDate = undefined;
		if (this.f.endDate.value !== '' && this.f.endDate.value !== null) {
			endDate = moment(this.f.endDate.value).format('YYYY-MM-DD') + 'T00:00:00.000Z';
		}
		if (endDate !== undefined && endDate.includes('1970-')) {
			endDate = undefined;
		}
		const schedule: Schedule = {
			...this.schedule,
			Name: this.f.name.value,
			Description: '',
			SceneryId: this.f.scenery.value.Id,
			Start: startDate,
			End: endDate,
			Tz: this.f.timezone.value,
			UserId: this.currentUser.Id,
			Hours: +startTime.split(':')[0],
			Minutes: +startTime.split(':')[1],
			EveryKey: this.f.everyKey.value !== EScheduleRepeat.NotRepeat ? this.f.everyKey.value : null,
			EveryValue: this.f.everyValue.value,
			Color: this.f.color.value
		};
		if (this.f.calendar.value.Id === -1) {
			schedule.CalendarId = null;
		} else {
			schedule.CalendarId = this.f.calendar.value.Id;
		}
		if (this.f.everyKey.value === EScheduleRepeat.Months) {
			schedule.MonthDay = this.f.monthDay.value;
		}
		if (this.f.everyKey.value === EScheduleRepeat.Weeks) {
			schedule.WeekDays = this.parseDays().toString();
		}

		if (schedule.Id) {
			this.store.dispatch(ScheduleActions.editSchedule({ schedule: schedule }));
		} else {
			this.store.dispatch(ScheduleActions.addSchedule({ schedule: schedule }));
		}
	}

	parseDays(): AbstractControl[] {
		const WDControl = this.f.weekDays as FormArray;
		const days = [];
		WDControl.controls.forEach((c, i) => {
			if (c.value) {
				days.push(i);
			}
		});
		return days;
	}

	get f(): any {
		return this.scheduleForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	resetClose(): void {
		this.store.dispatch(ScheduleActions.resetScheduleDialog());
		this.closeDialog();
	}

	getTime(hours: number, minutes: number): string {
		return moment().hour(hours).minute(minutes).format('HH:mm');
	}

	filterTimezone(event) {
		const filtered: any[] = [];
		const query = event.query;
		if (this.timezones) {
			for (let i = 0; i < this.timezones.length; i++) {
				const timezone = this.timezones[i];
				if (timezone.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
					filtered.push(timezone);
				}
			}
		}

		this.filteredTimezone = filtered;

	}

	onSelectCalendars() {
		this.store.select('calendar', 'exceptions')
			.pipe(
				map(exceptions => exceptions.filter(e =>
					e.CalendarId === this.f.calendar.value
				)),
				map(exclusions => exclusions.sort((a, b) => new Date(a.Date).valueOf() - new Date(b.Date).valueOf()))
			).subscribe((exceptions) => {
				if (this.f.calendar.value.Id !== -1) {
					this.exceptions = exceptions;
				}
			}
			);
	}

	colorSelected($event): void {
		this.f.color.setValue($event.color.hex);
	}

	getCalendar(): boolean {
		return (this.f.calendar.value !== null && this.f.calendar.value.Id !== -1);
	}

	enableConfirmButtons(): boolean {
		return this.activeIndex > 1;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-22rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	showErrorInvalidMessage(type: ScheduleDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showErrorConfirmInvalidMessage(type: ScheduleDialogComponent['f'], notEquivalentType: string): boolean {
		return StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, this.scheduleForm);
	}

	newShowErrors(type: ScheduleDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	getColorFirstTab(): string {
		this.canSwitchTab();
		return StandardNewValidatorStepTabs.getColorFirstTab();
	}

	getColorSecondTab(): string {
		return StandardNewValidatorStepTabs.getColorSecondTab(this.activeIndex);
	}

	getColorThirdTab(): string {
		return StandardNewValidatorStepTabs.getColorThirdTab(this.activeIndex);
	}

	showErrorsConfirmFields(type: ScheduleDialogComponent['f'], classType: string, notEquivalentType: string): string {
		return StandardNewValidator.showErrorsConfirmFields(type, classType, notEquivalentType, this.screenWidth, this.scheduleForm);
	}

	getSpaceBottomErrorMessage(type: ScheduleDialogComponent['f']): string {
		return (this.screenWidth > StandardDesignParameters.MAX_MD_WIDTH) && (!this.showErrorRequiredMessage(type)) ? " pb-5" : "";
	}

	showErrorRequiredMessage(type: ScheduleDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showErrorRequiredCheckBox(type: ScheduleDialogComponent["f"]): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}

	dynamicInputTextFormat(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	manageRespStyle(classType: string): string {
		return StandardPageLayout.manageRespStyle(classType, this.screenWidth);
	}

	manageRespStylewithError(type: ScheduleDialogComponent["f"], classType: string): string {
		return StandardNewValidator.manageRespStylewithError(type, classType, this.screenWidth);
	}
}
